import type { List } from "../lib/api.js";
import { itemPath, listPath, viewPath, type Route } from "../lib/router.js";
import { completedVisibility, type CompletedVisibility } from "./completed.js";
import type { MenuItem } from "./menu.js";
import { openShareList } from "./share.js";
import type { Popup, Shell } from "./ui.js";

/**
 * What a list screen may offer, and to whom (g72).
 *
 * The same screen draws the owner's list inside the account's shell and a
 * shared list on its own page, because the rows, the completed rules, the
 * drag and Details hold most of the product's hard-won behaviour and a second
 * copy of them would drift. What differs is said here, once, rather than as a
 * test of "is this shared" scattered through the screen.
 */

/**
 * A change that waits for a name (g72): a shared list's edit visitor who has
 * not said who they are is asked first, and the change runs once they have.
 */
export interface HeldWrite {
  /** The change, run once and only once. It re-reads what it needs. */
  run(): void;
  /** Whether the change still means what it meant when it was asked for. */
  valid(): boolean;
  /** The prompt was cancelled or failed for good: nothing was written. */
  cancelled?(): void;
  /** Where the focus goes back to when the prompt is cancelled. */
  returnFocusTo?(): HTMLElement | undefined;
  /** The popup the change was asked from: the prompt is a step of it, not a popup over it. */
  within?: Popup;
}

/**
 * Where every change on a list screen passes. `ready()` is true when a
 * change may run now; otherwise the screen puts the change back the way it
 * was and hands it to `hold`, which asks for a name and runs it after.
 */
export interface WriteGate {
  ready(): boolean;
  hold(write: HeldWrite): void;
}

/** The owner's gate: always open. */
export const OPEN_GATE: WriteGate = {
  ready: () => true,
  hold: (write) => write.run(),
};

export interface ListPolicy {
  /**
   * The list's own commands: renaming it from its heading, Edit list, Share,
   * Delete list and Order by category. Only its owner has them; a link never does.
   */
  owner: boolean;
  /**
   * Whether the rows offer any change at all. "view" draws a view link's
   * page: no checkbox to tick, no handle, no row menu, no Add, and Details
   * that only reads. It can move from "edit" to "view" while the list is on
   * screen, when a link stops working; never back.
   */
  controls(): "edit" | "view";
  onControls(fn: () => void): () => void;
  /** Whether completed rows are showing. */
  completed: CompletedVisibility;
  /**
   * Who is looking, when rows say who added them, or undefined when they
   * say nothing. `viewer` is the looker's creator id: their own rows stay
   * quiet.
   */
  attribution(list: List): { viewer: string | null | undefined } | undefined;
  gate: WriteGate;
  /** Share list: the owner's dialog, with its links (g73). A link has none. */
  share?(trigger: HTMLElement): void;
  /** What the list's menu offers besides Reorder, when the list is not the looker's own. */
  menu?(): MenuItem[];
  /**
   * What the list's menu holds for someone who may only read it, which has no
   * menu otherwise: an added list's Remove from my Lists inside the app (g137).
   */
  viewMenu?(): MenuItem[];
  /** Under the title: a shared page's line saying who shared it and what the link allows. */
  byline?: Node;
  /** Where selecting a row and closing it go. */
  paths: {
    list(): string;
    item(id: string): string;
    /** An item's note on its own page (g77): the owner's only, so a link's visitor has no View note. */
    view?(id: string): string;
    /** The route's selection when the route is this screen's, or undefined when it is not. */
    selection(route: Route): { itemId: string | undefined } | undefined;
  };
}

/** The owner's own list, inside the account's shell: everything, as it always was. */
export function ownerPolicy(shell: Shell, listId: string): ListPolicy {
  return {
    owner: true,
    controls: () => "edit",
    onControls: () => () => undefined,
    // A device view preference, this account's and this list's (C02).
    completed: completedVisibility({ userId: shell.user.id, view: "list", listId }),
    // Only while one of its links works: a private list is nobody else's,
    // and every row in it is theirs. The list's creator is its owner, since
    // only the owning account ever creates a list.
    attribution: (list) => (list.sharing?.view || list.sharing?.edit ? { viewer: list.creatorId } : undefined),
    gate: OPEN_GATE,
    share: (trigger) => openShareList(shell, listId, trigger),
    paths: {
      list: () => listPath(listId),
      item: (id) => itemPath(listId, id),
      view: (id) => viewPath(listId, id),
      selection: (route) => (route.name === "list" && route.id === listId ? { itemId: route.itemId } : undefined),
    },
  };
}
